import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip } from '@mui/material';
import { LockOutlined, CheckCircleOutlined, StarOutlined } from '@mui/icons-material';

const PLANS = [
  {
    name: 'Basic',
    key: 'basic',
    price: 'Free',
    color: '#64748B',
    features: ['Customer Management', 'Challan Tracking', 'Cylinder Management', 'Items & Goods', 'Reports', 'Passbook'],
  },
  {
    name: 'Silver',
    key: 'silver',
    price: '₹999/mo',
    color: '#2563EB',
    features: ['Everything in Basic', 'Estimates & Quotations', 'Sales Orders', 'Invoices', 'Payments', 'Purchases', 'Billing Statements', 'Credit/Debit Notes'],
  },
  {
    name: 'Gold',
    key: 'gold',
    price: '₹1999/mo',
    color: '#D97706',
    features: ['Everything in Silver', 'Advanced Analytics', 'Multi-branch Support', 'API Access', 'Priority Support'],
  },
];

export default function UpgradeModal({ open, onClose, currentPlan, lockedFeature, onUpgrade }) {
  const currentIdx = PLANS.findIndex((p) => p.key === currentPlan) || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <LockOutlined sx={{ fontSize: 40, color: '#D97706', mb: 1 }} />
        <Typography variant="h5" fontWeight={700}>Upgrade Your Plan</Typography>
        {lockedFeature && (
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            This feature requires a higher plan
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          {PLANS.map((plan, idx) => {
            const isCurrent = plan.key === currentPlan;
            const isLocked = idx > currentIdx;
            return (
              <Box
                key={plan.key}
                sx={{
                  flex: 1,
                  maxWidth: 260,
                  border: `2px solid ${isCurrent ? plan.color : '#E2E8F0'}`,
                  borderRadius: 2,
                  p: 2.5,
                  position: 'relative',
                  opacity: isLocked ? 1 : 0.7,
                  background: isCurrent ? `${plan.color}08` : '#fff',
                }}
              >
                {isCurrent && (
                  <Chip
                    label="Current Plan"
                    size="small"
                    sx={{ position: 'absolute', top: -10, right: 8, bgcolor: plan.color, color: '#fff', fontWeight: 600, fontSize: '0.65rem' }}
                  />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  {plan.key === 'gold' ? (
                    <StarOutlined sx={{ color: plan.color }} />
                  ) : plan.key === 'silver' ? (
                    <CheckCircleOutlined sx={{ color: plan.color }} />
                  ) : null}
                  <Typography fontWeight={700} fontSize="1.1rem">{plan.name}</Typography>
                </Box>
                <Typography variant="h6" fontWeight={800} mb={2}>{plan.price}</Typography>
                {plan.features.map((f) => (
                  <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <CheckCircleOutlined sx={{ fontSize: 14, color: plan.color }} />
                    <Typography variant="body2" fontSize="0.8rem">{f}</Typography>
                  </Box>
                ))}
                {isLocked && (
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{ mt: 2, bgcolor: plan.color, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: plan.color, opacity: 0.9 } }}
                    onClick={() => onUpgrade(plan.key)}
                  >
                    Upgrade to {plan.name}
                  </Button>
                )}
                {isCurrent && (
                  <Button fullWidth variant="outlined" disabled sx={{ mt: 2, textTransform: 'none' }}>
                    Current Plan
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Maybe Later</Button>
      </DialogActions>
    </Dialog>
  );
}
